import { useRef } from "react";

import { tokenize } from "@/lib/scoring/highlight";

import styles from "./Editor.module.css";

/**
 * The builder's code field: line numbers, syntax colour, one dependency (none).
 *
 * A transparent textarea over a painted copy of its own text. The textarea owns
 * typing, selection and scrolling; the layer underneath owns the colour; the
 * gutter counts the lines. They agree because they share one font, one line
 * height, one padding — and because `tokenize` returns the source unchanged.
 *
 * `singleLine` is the expression flavour: same colouring, no gutter, and Enter
 * is refused rather than smuggling a newline into a one-line rule.
 */
export function CodeEditor({
  value,
  rows,
  disabled,
  singleLine = false,
  placeholder,
  bad,
  onChange,
}: {
  value: string;
  rows?: number;
  disabled: boolean;
  singleLine?: boolean;
  placeholder?: string;
  bad?: boolean;
  onChange: (value: string) => void;
}) {
  const paintRef = useRef<HTMLPreElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);

  const lines = value.split("\n").length;
  const height = singleLine ? 1 : Math.max(rows ?? 10, lines);

  const align = (area: HTMLTextAreaElement) => {
    if (paintRef.current) {
      paintRef.current.scrollTop = area.scrollTop;
      paintRef.current.scrollLeft = area.scrollLeft;
    }
    if (gutterRef.current) gutterRef.current.scrollTop = area.scrollTop;
  };

  return (
    <div className={styles.codeEditor} data-single={singleLine || undefined} data-bad={bad || undefined}>
      {!singleLine && (
        <div ref={gutterRef} className={styles.codeGutter} aria-hidden="true">
          {Array.from({ length: height }, (_, i) => (
            <span key={i}>{i + 1}</span>
          ))}
        </div>
      )}
      <div className={styles.codeArea}>
        <pre ref={paintRef} className={styles.codePaint} aria-hidden="true">
          <code className={styles.tokenized}>
            {tokenize(value).map((token, i) => (
              <span key={i} data-tok={token.kind}>
                {token.text}
              </span>
            ))}
            {"\n"}
          </code>
        </pre>
        <textarea
          className={styles.codeText}
          value={value}
          disabled={disabled}
          spellCheck={false}
          autoCapitalize="off"
          autoComplete="off"
          wrap="off"
          rows={height}
          placeholder={placeholder}
          onChange={(e) => {
            onChange(singleLine ? e.target.value.replace(/\n/g, " ") : e.target.value);
            align(e.target);
          }}
          onScroll={(e) => align(e.currentTarget)}
          onKeyDown={(e) => {
            if (singleLine && e.key === "Enter") {
              e.preventDefault();
              return;
            }
            // Tab indents, as every code editor does. Escape-then-Tab still
            // moves focus, so the keyboard is not trapped.
            if (e.key === "Tab" && !e.shiftKey && !disabled) {
              e.preventDefault();
              const area = e.currentTarget;
              const { selectionStart, selectionEnd } = area;
              const next = `${value.slice(0, selectionStart)}  ${value.slice(selectionEnd)}`;
              onChange(next);
              requestAnimationFrame(() => {
                area.selectionStart = area.selectionEnd = selectionStart + 2;
              });
            }
          }}
        />
      </div>
    </div>
  );
}
